'use strict';

/**
 * @fileoverview Row VALUES in PostgreSQL's own error text (owner ruling 2026-10-05: a failing query's
 * log keeps the SQL shape, never a bound value).
 *
 * PostgreSQL answers a refused write with the data it refused: a unique or foreign-key violation's
 * `detail` is `Key (<columns>)=(<values>) …`, a not-null or check violation's is `Failing row contains
 * (<every column value>).`, and a data exception's MESSAGE quotes the offending input (`invalid input
 * syntax for type integer: "<input>"`). None of it is shaped like a secret, so no other family in
 * `redactSensitiveText` sees a name, an address or a password hash in it. Each template below is
 * PostgreSQL's (`errdetail` / `errmsg`); the value is replaced and everything an operator diagnoses
 * with stays — the columns, the table, the type.
 *
 * WHERE A VALUE ENDS. PostgreSQL does not escape what it prints, so a value may hold a `)` or a `"`.
 * A value therefore runs to the LAST closing text within its window, never the first: a value that
 * contains a closing phrase is redacted whole, and the cost is that text between two details in one
 * string can go with the first. An opening with no close at all is redacted to the end of its window.
 *
 * LINEAR. Plain `indexOf` scans over bounded windows: every pass moves forward past what it judged, so
 * the cost is proportional to the text (`tests/redactDatabaseValues.test.js` holds it on adversarial
 * input).
 *
 * @module @puffin-core/redaction/redactDatabaseValues
 */

/** The longest value searched for its close. */
const VALUE_WINDOW = 4096;
/** A failing row prints every column. */
const ROW_WINDOW = 16384;
/** How far back `Key (` may sit from its `)=(`: the column list. */
const KEY_GUARD_WINDOW = 1024;
/** How far a type or enum name may run before its `: "`. */
const LEAD_WINDOW = 128;

/**
 * Templates found by their OPENING text. `lead` is a second text that must follow the opening within
 * {@link LEAD_WINDOW} (the value starts after it); `guard` must appear within
 * {@link KEY_GUARD_WINDOW} before the opening; `closes` are the texts a value may end at, and `close`
 * the fallback when none of them is in the window.
 */
const OPENING_TEMPLATES = Object.freeze([
  {
    open: ')=(',
    guard: 'ey (',
    closes: [
      ') already exists',
      ') is not present in table',
      ') is still referenced from table',
      ') conflicts with',
      ') is duplicated',
    ],
    close: ')',
    window: VALUE_WINDOW,
  },
  { open: 'ailing row contains (', closes: [], close: ')', window: ROW_WINDOW },
  {
    open: 'invalid input syntax for type ',
    lead: ': "',
    closes: [],
    close: '"',
    window: VALUE_WINDOW,
  },
  {
    open: 'invalid input value for enum ',
    lead: ': "',
    closes: [],
    close: '"',
    window: VALUE_WINDOW,
  },
  { open: 'field value out of range: "', closes: [], close: '"', window: VALUE_WINDOW },
  { open: 'malformed array literal: "', closes: [], close: '"', window: VALUE_WINDOW },
  { open: 'malformed range literal: "', closes: [], close: '"', window: VALUE_WINDOW },
  { open: 'malformed record literal: "', closes: [], close: '"', window: VALUE_WINDOW },
]);

/** Templates found by their CLOSING text; the value starts after the nearest `open` before it. */
const CLOSING_TEMPLATES = Object.freeze([
  { open: 'value "', close: '" is out of range' },
  { open: 'Token "', close: '" is invalid' },
]);

/** One text each template REQUIRES, for the presence pre-check. */
const TRIGGERS = Object.freeze([
  ...OPENING_TEMPLATES.map((template) => template.open),
  ...CLOSING_TEMPLATES.map((template) => template.close),
]);

/**
 * Can any template match this text? A skip here can never lose a match: each trigger is text its
 * template requires.
 *
 * @param {string} text
 * @returns {boolean}
 */
function hasDatabaseValueTrigger(text) {
  return TRIGGERS.some((trigger) => text.includes(trigger));
}

/**
 * Where the value that starts a window ends: the LAST of the template's closing texts, else the last
 * fallback close, else the whole window.
 *
 * @param {string} windowText - The text from the value's first character, at most one window long
 * @param {{closes: string[], close: string}} template
 * @returns {number} The value's length
 */
function valueLength(windowText, template) {
  const named = Math.max(-1, ...template.closes.map((close) => windowText.lastIndexOf(close)));
  if (named !== -1) return named;
  const fallback = windowText.lastIndexOf(template.close);
  return fallback === -1 ? windowText.length : fallback;
}

/**
 * Where the value begins after an opening at `at`, or -1 when the opening is not this template's.
 *
 * @param {string} text
 * @param {number} at - Index of the opening
 * @param {{open: string, lead?: string, guard?: string}} template
 * @returns {number}
 */
function valueStart(text, at, template) {
  const afterOpen = at + template.open.length;
  if (
    template.guard &&
    !text.slice(Math.max(0, at - KEY_GUARD_WINDOW), at).includes(template.guard)
  ) {
    return -1;
  }
  if (!template.lead) return afterOpen;
  const lead = text.slice(afterOpen, afterOpen + LEAD_WINDOW).indexOf(template.lead);
  return lead === -1 ? -1 : afterOpen + lead + template.lead.length;
}

/**
 * Replace every value one opening template finds.
 *
 * @param {string} text
 * @param {string} placeholder
 * @param {Object} template - A member of {@link OPENING_TEMPLATES}
 * @returns {string}
 */
function redactFromOpening(text, placeholder, template) {
  let result = '';
  let from = 0;
  for (;;) {
    const at = text.indexOf(template.open, from);
    if (at === -1) return result + text.slice(from);
    const start = valueStart(text, at, template);
    if (start === -1) {
      const next = at + template.open.length;
      result += text.slice(from, next);
      from = next;
    } else {
      const length = valueLength(text.slice(start, start + template.window), template);
      result += `${text.slice(from, start)}${placeholder}`;
      from = start + length;
    }
  }
}

/**
 * Replace every value one closing template finds.
 *
 * @param {string} text
 * @param {string} placeholder
 * @param {{open: string, close: string}} template - A member of {@link CLOSING_TEMPLATES}
 * @returns {string}
 */
function redactBeforeClosing(text, placeholder, template) {
  let result = '';
  let from = 0;
  for (;;) {
    const close = text.indexOf(template.close, from);
    if (close === -1) return result + text.slice(from);
    const floor = Math.max(from, close - VALUE_WINDOW);
    const open = text.slice(floor, close).lastIndexOf(template.open);
    const next = close + template.close.length;
    result +=
      open === -1
        ? text.slice(from, next)
        : `${text.slice(from, floor + open + template.open.length)}${placeholder}${template.close}`;
    from = next;
  }
}

/**
 * Replace the row values PostgreSQL wrote into an error text.
 *
 * @param {string} text - A non-empty string
 * @param {string} placeholder - The replacement for a value
 * @returns {string}
 */
function redactDatabaseValues(text, placeholder) {
  let result = text;
  for (const template of OPENING_TEMPLATES) {
    result = redactFromOpening(result, placeholder, template);
  }
  for (const template of CLOSING_TEMPLATES) {
    result = redactBeforeClosing(result, placeholder, template);
  }
  return result;
}

module.exports = { redactDatabaseValues, hasDatabaseValueTrigger };
